import { PaintBrush } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import ReactFlow, { ControlButton, Controls } from 'reactflow';
import FormStyleSettings from '../../../components/Forms/FormStyleSettings';
import { useAuthStore } from '../../../store/storeAuth';
import { formNodeTypes } from '../../../util/nodeTypes';
import useCopyPaste from '../useCopyPaste';
import HelperLinesRenderer from './HelperLines';

const isNodeOverlapping = (node1: any, node2: any) => {
  const rect1 = node1.position;
  const rect2 = node2.position;
  const width1 = node1.width || 150; 
  const height1 = node1.height || 40; 
  const width2 = node2.width || 150;
  const height2 = node2.height || 40;

  return (
    rect1.x < rect2.x + width2 &&
    rect1.x + width1 > rect2.x &&
    rect1.y < rect2.y + height2 &&
    rect1.y + height1 > rect2.y
  );
};

const findNewPosition = (node: any, otherNodes: any[]) => {
  const padding = 20;
  const width = node.width || 150;
  const height = node.height || 40;
  
  const positions = [
    { x: node.position.x + width + padding, y: node.position.y },
    { x: node.position.x - width - padding, y: node.position.y },
    { x: node.position.x, y: node.position.y + height + padding },
    { x: node.position.x, y: node.position.y - height - padding },
  ];

  for (const pos of positions) {
    const newNode = { ...node, position: pos };
    const hasOverlap = otherNodes.some(otherNode => 
      otherNode.id !== node.id && isNodeOverlapping(newNode, otherNode)
    );
    
    if (!hasOverlap) {
      return pos;
    }
  }

  return node.position;
};

const FormBuilderFlow = ({
  nodes,
  onNodesChange,
  onDragOver,
  onDrop,
  setReactFlowInstance,
  helperLineHorizontal,
  helperLineVertical,
  form,
  setForm,
}: any) => {
  const { user, setAuthUser } = useAuthStore(state => state);
  const [formStyle, setFormStyle] = useState({
    backgroundColor: form?.formStyle?.backgroundColor || 'rgb(231, 240, 255)',
    backgroundImage: form?.formStyle?.backgroundImage || '',
    horizontalSettings: {
      enabled: form?.formStyle?.horizontalSettings?.enabled ?? true,
      gap: form?.formStyle?.horizontalSettings?.gap || 16,
    },
    verticalSettings: {
      enabled: form?.formStyle?.verticalSettings?.enabled ?? true,
      gap: form?.formStyle?.verticalSettings?.gap || 16,
    },
  });

  const [colorModalOpen, setColorModalOpen] = useState(false);

  const { cut, copy, paste, bufferedNodes } = useCopyPaste();

  const canCopy = nodes.some(({ selected }: any) => selected);
  const canPaste = bufferedNodes.length > 0;

  const theme = user?.theme;

  const handleNodeDragStop = useCallback((event: any, node: any) => {
    if (node.type === 'linesNode') {
      return;
    }

    const otherNodes = nodes.filter((n: { id: string; type: string }) => n.id !== node.id && n.type !== 'linesNode');
    const overlappingNode = otherNodes.find((n: any) => isNodeOverlapping(node, n));
    
    if (overlappingNode) {
      const newPosition = findNewPosition(node, otherNodes);
      onNodesChange([
        {
          type: 'position',
          id: node.id,
          position: newPosition,
        },
      ]);
    }
  }, [nodes, onNodesChange]);

  return (
    <ReactFlow
      style={{
        background: theme === 'dark' ? '#475467' : formStyle?.backgroundColor,
        borderRadius: '8px',
      }}
      // className={theme === 'dark' ? 'dark-theme' : 'light-theme'}
      nodes={nodes}
      onNodesChange={onNodesChange}
      snapToGrid
      nodeTypes={formNodeTypes as any}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onInit={setReactFlowInstance}
      proOptions={{ account: 'paid-pro', hideAttribution: true }}
      fitView //   edgeTypes={edgeTypes as any}Learn more about JS/TS refactorings
      maxZoom={1}
      minZoom={0.3}
      // onPointerMove={onMouseMove}
      // panOnScroll={true}
      // panOnScrollMode={PanOnScrollMode.Vertical}
      // panOnDrag={false}

      // background fixed
      onNodeDragStop={handleNodeDragStop}
    >
      <HelperLinesRenderer
        horizontal={helperLineHorizontal}
        vertical={helperLineVertical}
      />

      {/* <Panel
        style={{
          display: 'flex',
        }}
        position="top-left"
      >
        <Button onClick={() => cut()} disabled={!canCopy}>
          cut
        </Button>
        <Button onClick={() => copy()} disabled={!canCopy}>
          copy
        </Button>
        <Button onClick={() => paste({ x: 0, y: 0 })} disabled={!canPaste}>
          paste
        </Button>
      </Panel> */}
      <Controls
        position="top-right"
        showFitView={false}
        showInteractive={false}
        showZoom={false}
        style={{
          boxShadow: 'none',
          // size: '40px',
        }}
      >
        <ControlButton
          onClick={() => {
            setColorModalOpen(true);
          }}
        >
          <PaintBrush style={{}} size={24} />
        </ControlButton>
      </Controls>
      <FormStyleSettings
        colorModalOpen={colorModalOpen}
        setColorModalOpen={setColorModalOpen}
        formStyle={formStyle}
        setFormStyle={setFormStyle}
        setAuthUser={setAuthUser}
        form={form}
        setForm={setForm}
        isForm={true}
      />
    </ReactFlow>
  );
};

export default FormBuilderFlow;
